/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import {useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {
    Box,
    Text,
    Heading,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import EinsteinTaSlotHtml from '../../../components/content-slot/einstein-ta-slot-html'
import {productCarouselStyles} from '../../../theme/components/project/product-carousel'

const EmptySearchResults = ({searchQuery, category}) => {
    const intl = useIntl()
    const styles = useMultiStyleConfig('EmptySearchResults')

    if (searchQuery === 'emptysearch') {
        searchQuery = ''
    }

    const containerStyles = {
        padding: {
            base: '0 10px',
            sm: '0 20px',
            lg: '0 64px'
        },
        maxWidth: {
            base: '100%',
            lg: '1920px'
        },
        margin: '0 auto'
    }

    return (
        <Box
            data-testid={'sf-product-empty-list-page'}
            className={'search-results'}
            {...styles.emptyPageContainer}
        >
            <Box className={'empty-search-results-container'} {...styles.emptyPageContent}>
                <Box className={'category-search-title-block'}>
                    <Heading
                        as={'h1'}
                        borderBottom={'1px solid black'}
                        padding={'32px 0'}
                        textAlign={'center'}
                    >
                        {intl.formatMessage(
                            {
                                id: 'search.results.for',
                                defaultMessage: 'Search results for "{query}"'
                            },
                            {
                                query: category?.name ? category?.name : searchQuery
                            }
                        )}
                    </Heading>
                </Box>
                <Text as="p" fontSize={'sm'} padding={'24px 0'}>
                    {intl.formatMessage({
                        id: 'search_suggestions.product_count',
                        defaultMessage: '0 Products'
                    })}
                </Text>
                <Heading as={'h2'} {...styles.emptyPageHeading}>
                    {intl.formatMessage(
                        {
                            id: 'no_results.info.cant_find_anything_for_category',
                            defaultMessage: 'No Results for "{query}" Found.'
                        },
                        {
                            query: category?.name ? category?.name : searchQuery
                        }
                    )}
                </Heading>
                <Heading as="h4" {...styles.emptyPageSubHeading}>
                    {intl.formatMessage({
                        id: 'search_suggestions.suggestions',
                        defaultMessage: 'Suggestions'
                    })}
                </Heading>
                <Text as="p" fontSize={'sm'}>
                    {intl.formatMessage({
                        id: 'search_suggestions.misspellings',
                        defaultMessage: 'Check for misspellings.'
                    })}
                </Text>
                <Text as="p" fontSize={'sm'}>
                    {intl.formatMessage({
                        id: 'search_suggestions.redundantkeywords',
                        defaultMessage: 'Remove possible redundant keywords (ie. "products")'
                    })}
                </Text>
                <Text as="p" fontSize={'sm'}>
                    {intl.formatMessage({
                        id: 'search_suggestions.useotherwords',
                        defaultMessage: 'Use other words to describe what you are searching for.'
                    })}
                </Text>
                <Box
                    className={'empty-results-suggestions-block'}
                    {...styles.emptyResultsSuggestionsBlock}
                ></Box>
            </Box>
            <Box
                backgroundImage={`url(${getAssetUrl('static/img/bg_shop_bottom_halftone.png')})`}
                backgroundPosition="0 100%"
                backgroundSize="auto"
                backgroundRepeat="repeat-x"
                sx={productCarouselStyles}
            >
                <Box className={'einsteinRecommendations'} sx={containerStyles}>
                    <EinsteinTaSlotHtml
                        slotId={'no-results-recommendations-m'}
                        context={'global'}
                        type={'html'}
                    />
                    <EinsteinTaSlotHtml
                        slotId={'plp-product-recommendations-m'}
                        context={'global'}
                        type={'html'}
                    />
                    <EinsteinTaSlotHtml
                        slotId={'plp-product-recommendations2-m'}
                        context={'global'}
                        type={'html'}
                    />
                    <EinsteinTaSlotHtml
                        slotId={'plp-product-recommendations3-m'}
                        context={'global'}
                        type={'html'}
                    />
                </Box>
            </Box>
        </Box>
    )
}

EmptySearchResults.propTypes = {
    searchQuery: PropTypes.string,
    category: PropTypes.object
}

export default EmptySearchResults
